import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import {
    ArrowRight, HeartHandshake, Sun, Video, Utensils, Dumbbell, Flower2, Brain,
} from "lucide-react";
import { track, EVENTS } from "@/lib/analytics";

/* Community Partners teaser
 *
 * Originally lived on /help-finder (Resources). The user asked it move to
 * /wellness (Caregiver Wellness) because partner help is fundamentally a
 * caregiver-self-care concern, not a clinical/program resource. We kept the
 * component standalone (this file) so any page that wants it can drop it in.
 *
 * What it does:
 *   - Shows 6 example partner categories (spas, gyms, therapy, virtual
 *     doctors, meal services, respite) with a "Coming soon" badge so
 *     caregivers know what's being added.
 *   - "Browse partners" CTA → /resources/community-partners (live directory)
 *   - "Become a partner" CTA → opens an inline application form that posts
 *     to /api/partners/interest. Leads land in Admin → Partner Leads and
 *     can be one-click approved into the directory.
 *   - "Suggest a partner" → /refer (caregivers nominating partners they love)
 */

const PARTNER_CATEGORIES = [
    { key: "wellness",   label: "Spas & wellness",      Icon: Flower2,      tone: "bg-coral-light text-coral-dark",         example: "Massage · facials · sleep clinics" },
    { key: "gyms",       label: "Gyms & movement",       Icon: Dumbbell,     tone: "bg-stage_early/40 text-stage_early-dark", example: "Caregiver-friendly memberships · classes" },
    { key: "therapy",    label: "Caregiver therapy",     Icon: Brain,        tone: "bg-teal-soft text-teal-dark",            example: "1:1 therapists · grief counsellors" },
    { key: "telehealth", label: "Virtual doctors",       Icon: Video,        tone: "bg-ml_blue-light text-ml_blue-dark",     example: "Same-day video visits · refills" },
    { key: "meals",      label: "Meal services",         Icon: Utensils,     tone: "bg-yellow-light text-yellow-dark",       example: "Senior-friendly meal kits · delivery" },
    { key: "respite",    label: "Respite teams",         Icon: Sun,          tone: "bg-sage-light text-sage-dark",           example: "Vetted in-home & overnight relief" },
];

export default function CommunityPartnersTeaser() {
    const [applyOpen, setApplyOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [form, setForm] = useState({ org_name: "", email: "", role: "", notes: "" });
    const [err, setErr] = useState("");

    const submit = async (e) => {
        e.preventDefault();
        if (submitting) return;
        setErr("");
        if (!form.org_name.trim() || !form.email.trim()) {
            setErr("Org name and email are required.");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/partners/interest", {
                email: form.email.trim(),
                org_name: form.org_name.trim(),
                role: (form.role || "").trim(),
                notes: (form.notes || "Submitted via Community Partners section on /wellness").trim(),
            });
            track(EVENTS.PARTNER_REFERRAL, {
                source: "community_partners_teaser",
                org_name: form.org_name.trim(),
                role: (form.role || "").trim(),
            });
            setSubmitted(true);
        } catch (e2) {
            setErr(e2?.response?.data?.detail || "Couldn't submit just now — please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section
            id="community-partners"
            data-anchor="community-partners"
            className="ml-card p-5 md:p-6 bg-gradient-to-br from-teal-soft/40 via-cream-light to-coral-light/30 border border-teal-light"
            data-testid="wellness-community-partners-section"
        >
            <div className="flex items-start gap-3 mb-1 flex-wrap">
                <div className="w-10 h-10 rounded-2xl bg-white text-teal-dark flex items-center justify-center shrink-0 border border-teal-light">
                    <HeartHandshake size={18} strokeWidth={1.7} />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-xs uppercase tracking-wider font-semibold text-teal-dark">Community partners</p>
                        <span className="inline-flex items-center text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-2xl bg-coral-light text-coral-dark border border-coral-softer">
                            Coming soon
                        </span>
                    </div>
                    <h2 className="mt-0.5 text-xl md:text-2xl font-heading font-medium text-[#12776F]">
                        Take care of yourself — partners we&apos;re onboarding for caregivers
                    </h2>
                    <p className="mt-1 text-sm text-[#4C5551] leading-relaxed max-w-2xl">
                        Caregiver burnout is the #1 risk we hear about. We&apos;re curating a
                        directory of community partners — vetted, caregiver-friendly, often
                        discounted — so the help that&apos;s for <em>you</em> lives in one place.
                    </p>
                </div>
            </div>

            <ul
                className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-3"
                data-testid="community-partner-examples"
            >
                {PARTNER_CATEGORIES.map(({ key, label, Icon, tone, example }) => (
                    <li
                        key={key}
                        data-testid={`community-partner-example-${key}`}
                        className="rounded-2xl border border-cream-dark bg-white p-3 flex items-start gap-3"
                    >
                        <span className={`inline-flex items-center justify-center w-9 h-9 rounded-2xl shrink-0 ${tone}`}>
                            <Icon size={16} strokeWidth={1.8} />
                        </span>
                        <div className="min-w-0">
                            <p className="text-sm font-medium text-[#1F2624] truncate">{label}</p>
                            <p className="text-xs text-[#6E7A74] mt-0.5 leading-snug">{example}</p>
                        </div>
                    </li>
                ))}
            </ul>

            <div className="mt-4 flex flex-wrap items-center gap-2">
                <Link
                    to="/resources/community-partners"
                    data-testid="community-partners-browse"
                    className="ml-btn-primary !min-h-[40px] !px-4 !py-2 !text-sm inline-flex items-center gap-1.5"
                >
                    Browse partners <ArrowRight size={14} strokeWidth={1.8} />
                </Link>
                <button
                    type="button"
                    onClick={() => { setApplyOpen(o => !o); setSubmitted(false); setErr(""); }}
                    data-testid="community-partners-apply-toggle"
                    aria-expanded={applyOpen}
                    className="!min-h-[40px] !px-4 !py-2 !text-sm inline-flex items-center gap-1.5 rounded-2xl font-medium bg-coral-dark text-white hover:bg-coral transition-colors"
                >
                    {applyOpen ? "Hide partner form" : "Become a partner"}
                </button>
                <Link
                    to="/refer"
                    data-testid="community-partners-suggest"
                    className="!min-h-[40px] !px-4 !py-2 !text-sm inline-flex items-center gap-1.5 rounded-2xl font-medium bg-white border border-cream-dark text-[#1F2624] hover:border-teal-dark hover:text-teal-dark transition-colors"
                >
                    Suggest a partner
                </Link>
            </div>

            {applyOpen && (
                <div className="mt-5 rounded-2xl border border-cream-dark bg-white p-4 md:p-5" data-testid="community-partners-apply-panel">
                    {submitted ? (
                        <div className="text-center py-4" data-testid="community-partners-apply-thanks">
                            <p className="text-base font-heading font-medium text-teal-dark">Thanks — we&apos;ll be in touch.</p>
                            <p className="text-sm text-[#4C5551] mt-1.5">
                                Ashlee usually replies within 24 hours. We&apos;ll outline what onboarding looks like
                                and how we feature partners across the platform.
                            </p>
                            <button
                                type="button"
                                onClick={() => { setApplyOpen(false); setSubmitted(false); setForm({ org_name: "", email: "", role: "", notes: "" }); }}
                                data-testid="community-partners-apply-close"
                                className="mt-3 text-xs font-medium text-[#4C5551] underline"
                            >
                                Close
                            </button>
                        </div>
                    ) : (
                        <form onSubmit={submit} className="space-y-3" data-testid="community-partners-apply-form">
                            <div className="flex items-center gap-2 mb-1">
                                <HeartHandshake size={16} className="text-coral-dark" />
                                <p className="text-sm font-heading font-medium text-[#1F2624]">
                                    Are you a spa, gym, therapist, meal service, or care team?
                                </p>
                            </div>
                            <p className="text-xs text-[#4C5551] -mt-1">
                                Tell us about your offering — Ashlee personally reviews every application.
                            </p>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <label className="block">
                                    <span className="block text-xs uppercase tracking-wider font-semibold text-[#6E7A74] mb-1">Organization</span>
                                    <input
                                        type="text"
                                        value={form.org_name}
                                        onChange={(e) => setForm(f => ({ ...f, org_name: e.target.value }))}
                                        data-testid="community-partners-apply-org"
                                        required
                                        className="w-full px-3 py-2 rounded-2xl border border-cream-dark bg-white text-sm focus:outline-none focus:ring-2 focus:ring-teal-soft"
                                        placeholder="e.g. Stillwater Wellness Spa"
                                    />
                                </label>
                                <label className="block">
                                    <span className="block text-xs uppercase tracking-wider font-semibold text-[#6E7A74] mb-1">Email</span>
                                    <input
                                        type="email"
                                        value={form.email}
                                        onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))}
                                        data-testid="community-partners-apply-email"
                                        required
                                        className="w-full px-3 py-2 rounded-2xl border border-cream-dark bg-white text-sm focus:outline-none focus:ring-2 focus:ring-teal-soft"
                                        placeholder="you@yourcompany.com"
                                    />
                                </label>
                                <label className="block md:col-span-2">
                                    <span className="block text-xs uppercase tracking-wider font-semibold text-[#6E7A74] mb-1">Your role (optional)</span>
                                    <input
                                        type="text"
                                        value={form.role}
                                        onChange={(e) => setForm(f => ({ ...f, role: e.target.value }))}
                                        data-testid="community-partners-apply-role"
                                        className="w-full px-3 py-2 rounded-2xl border border-cream-dark bg-white text-sm focus:outline-none focus:ring-2 focus:ring-teal-soft"
                                        placeholder="e.g. Founder, Director of Partnerships"
                                    />
                                </label>
                                <label className="block md:col-span-2">
                                    <span className="block text-xs uppercase tracking-wider font-semibold text-[#6E7A74] mb-1">What do you offer caregivers?</span>
                                    <textarea
                                        rows={3}
                                        value={form.notes}
                                        onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))}
                                        data-testid="community-partners-apply-notes"
                                        className="w-full px-3 py-2 rounded-2xl border border-cream-dark bg-white text-sm focus:outline-none focus:ring-2 focus:ring-teal-soft"
                                        placeholder="A short pitch — type of service, any caregiver discount you'd offer, locations covered, etc."
                                    />
                                </label>
                            </div>
                            {err && (
                                <p className="text-xs text-coral-dark bg-coral-light/60 border border-coral-softer rounded-2xl px-3 py-2" data-testid="community-partners-apply-error">
                                    {err}
                                </p>
                            )}
                            <div className="flex items-center gap-2 pt-1">
                                <button
                                    type="submit"
                                    disabled={submitting}
                                    data-testid="community-partners-apply-submit"
                                    className="ml-btn-primary !min-h-[40px] !px-4 !py-2 !text-sm disabled:opacity-60"
                                >
                                    {submitting ? "Sending…" : "Send application"}
                                </button>
                                <span className="text-xs text-[#6E7A74]">No spam. We reply by email.</span>
                            </div>
                        </form>
                    )}
                </div>
            )}
        </section>
    );
}
